/**
 * Input mapping — raw keyboard/mouse -> InputCommand. (§4.1 + house scheme)
 *
 * Split in two so the mapping is unit-testable without a DOM:
 *   - `InputState`        the DOM-free raw state + edge latches. The DOM
 *                         listeners (or a test) call its `feed*` methods.
 *   - `buildInputCommand` pure: (state, map, ctx) -> InputCommand, consuming the
 *                         one-shot latches and applying curl decay.
 *   - `InputManager`      wires real DOM events + pointer lock onto an InputState.
 *
 * Charge model (§4.1): shoot/pass are HOLD-to-charge. The action flag is emitted
 * once, on RELEASE, with the accumulated hold duration — never while held, or the
 * sim's resolveKicks (ball.ts) would strike the ball on the first held tick with
 * ~zero charge. Lateral mouse motion during a shoot charge accumulates into
 * `curl` ("curve the mouse and the ball curves, but not much"), clamped to
 * [-1, 1] and decaying to 0 once the charge ends.
 *
 * Mouse buttons (2026-07-18, defects #11/#12 — LMB shoot / RMB pass) are NOT a
 * parallel input path: `feedMouseDown`/`feedMouseUp` translate `MouseEvent.button`
 * to a synthetic code via `mouseCode()` and hand off to `feedKeyDown`/`feedKeyUp`
 * — the exact same held-state Set, action switch, and charge/curl/bicycle-kick
 * mechanics that keyboard codes drive. A mouse button is "just another code" as
 * far as this class is concerned; which action it drives is entirely a
 * `ControlMap` lookup, so rebinding shoot/pass onto a different mouse button (or
 * even a keyboard key) needs no code change here.
 */

import {
  InputFlags,
  SkillMove,
  TacticalCommand,
  emptyInput,
  type InputCommand,
} from '@fg/shared/sim';
import { clamp, wrapAngle } from '@fg/shared/math';
import { mouseCode, type BindableAction, type ControlMap } from './controlMap.js';

// ── Client-side feel tuning (NOT sim tunables — pure input feel) ─────────────
/**
 * Radians of camera/aim rotation per pixel of pointer-lock mouse motion.
 *
 * Tuned for comfort by real browser playtest (2026-07-23): the reference-route
 * 0.0028 still read as too fast for normal desk movement, so the default is now
 * 0.0018 rad/px (aim ≈0.78× that for finer shot/pass control). The settings
 * screen exposes a WIDE range (0.0007–0.0040) so fast-flick players can raise it;
 * the default is deliberately on the slower side. Applied ONCE to the raw
 * `movementX`, with NO delta-time and NO device-pixel-ratio scaling.
 *
 * Frame-rate independence: a mouse `movementX` is already a per-EVENT pixel
 * delta, not a per-second rate, so it must NEVER be multiplied by dt — the sum of
 * every movementX across a sweep is the same total pixels at 60 FPS or 180 FPS,
 * so the yaw turned is identical. (The camera's own smoothing is dt-correct and
 * lives separately in render/camera.ts.)
 */
export const DEFAULT_MOUSE_SENSITIVITY = 0.0018;
export const DEFAULT_AIM_SENSITIVITY = 0.0014;
/**
 * A movement request within this angle of the current facing TURNS the body
 * (ordinary steering — W/A/D and every forward diagonal). Beyond it the facing
 * holds and the player backpedals/strafes instead of spinning 180° to run at
 * the camera (the filmed defect). 100°: a pure sidestep (90°) still steers,
 * only genuinely backward-ish requests hold. Sprint always commits the turn.
 */
export const BACKPEDAL_TURN_COMMIT_RAD = (100 * Math.PI) / 180;
/**
 * The same cone, WIDER, for a player who has no ball at his feet.
 *
 * Owner: *"remove player running backward when player click left right, move him
 * in that direction naturally when there is no ball at his feet"*.
 *
 * Holding the facing is a BALL-PROTECTION move. A carrier who backs out of
 * pressure keeps his body between the defender and the ball, and that is what
 * the 100° cone buys. A player with nothing at his feet has nothing to shield:
 * he just goes where he is told, and turning to face it is what a footballer
 * does. Running the same narrow cone for both is what put him into a
 * back-shuffle for an ordinary change of direction.
 *
 * 150°, not 180°, because a request that IS straight back is still a backpedal —
 * a defender giving ground while watching play does not spin round, and
 * `M_Backpedal` exists for exactly that. Only left, right and the back-diagonals
 * change behaviour.
 */
export const FREE_TURN_COMMIT_RAD = (150 * Math.PI) / 180;
/*
 * Movement is CAMERA-RELATIVE (modern 3rd-person), replacing the old tank scheme
 * (A/D rotate) — see buildInputCommand. WASD is a direction in the camera's ground
 * plane; the sim turns the body toward it. No client-side rotation constants are
 * needed: the turn is the sim's (turnRateFor, §3.3), the client only points.
 */
/** Curl accumulated per pixel of lateral mouse motion while charging a shot. */
const CURL_GAIN = 0.0055;
/** Lift bias step per wheel notch. */
const WHEEL_LIFT_STEP = 0.15;
/** Curl relaxes to zero over ~this many ms once a charge ends. */
const CURL_DECAY_TIME_MS = 180;
/**
 * Bicycle-kick compose window (house controls v2, default rebound to C in
 * v3 — §6; B still works if a player rebinds 'bicycleModifier' back onto it):
 * a shoot RELEASE while the bicycle modifier is held counts as a bicycle
 * attempt if the player is locally believed airborne — i.e. jump is still
 * held, or was pressed within this window. The client only REQUESTS: the sim
 * is the authority on legality (ball airborne in the overhead envelope,
 * player state, …); an illegal request falls through to a normal shot
 * resolution or nothing.
 */
export const BICYCLE_JUMP_WINDOW_MS = 400;
/**
 * §5 middle-mouse team commands. A first middle-click opens this window; a second
 * click inside it is a DOUBLE (pass-to-me), the window lapsing with no second
 * click is a SINGLE (team circulate). 300 ms is the house double-click window.
 */
export const MIDDLE_DOUBLE_WINDOW_MS = 300;
/** After either command resolves, ignore further middle-clicks for this long so a
 *  fumbled extra click cannot spam the team. */
export const MIDDLE_COMMAND_COOLDOWN_MS = 450;

/**
 * How many sim ticks an unserved shot/pass keeps asking to be played (60 Hz).
 *
 * 24 ticks = 0.4 s. Sized from the sprint carry cycle: the ball is measured out
 * of foot reach for 22 of every 23 ticks while running, so the window must span a
 * full knock cycle with margin or the buffer would still miss the touch. Kept
 * SHORT deliberately — much longer and a shot you thought you had abandoned fires
 * a second later, which feels worse than a dropped input.
 */
export const STRIKE_BUFFER_TICKS = 24;

/**
 * Pure compose test for InputFlags.BicycleKick, extracted so it is testable
 * without a DOM or a clock. `lastJumpDownMs < 0` means jump was never pressed.
 */
export function bicycleComposeActive(
  bicycleHeld: boolean,
  jumpHeld: boolean,
  lastJumpDownMs: number,
  nowMs: number,
): boolean {
  if (!bicycleHeld) return false;
  if (jumpHeld) return true; // key still down = mid-jump belief
  return lastJumpDownMs >= 0 && nowMs - lastJumpDownMs <= BICYCLE_JUMP_WINDOW_MS;
}

/**
 * Pure compose test for InputFlags.ThroughBall on a PASS release (house
 * controls v3, §6 — R = loft/cross modifier): R(hold) + RMB(pass) charge and
 * release requests a lofted ball, or a cross when taken from wide + advanced
 * areas (ball.ts's buildPass already treats ThroughBall this way — see its
 * header). Trivial today (no window/latch like the bicycle compose needs),
 * but kept as its own named, testable function for the same reason
 * bicycleComposeActive is one: the call site (feedKeyUp's 'pass' case) reads
 * as intent, not a bare boolean, and a future refinement (e.g. a grace window
 * like bicycle's) only touches this one function.
 *
 * ⚠ This must ONLY ever be OR'd onto the SAME one-shot Pass event, never held
 * live independent of an actual Pass release. Unlike SkillModifier (safe to
 * hold every tick — nothing outside buildShot reads it), ThroughBall sits in
 * rules/fsm.ts's TAKE_MASK, a restart "does this input want to play" test; a
 * bare held ThroughBall bit with no Pass would look like the taker committed
 * to the restart just from holding R.
 */
export function loftComposeActive(loftHeld: boolean): boolean {
  return loftHeld;
}

/** Context the loop supplies each build: camera basis + local possession. */
export interface InputContext {
  /** Unit ground-plane "forward" (W) direction in world XZ — camera-relative move. */
  forwardX: number;
  forwardZ: number;
  /** Unit ground-plane "right" (D) direction in world XZ. */
  rightX: number;
  rightZ: number;
  /** Does the controlled player possess the ball? Picks E's meaning (§4.1). */
  possessing: boolean;
  /** Controlled player's current ground speed (m/s). Scales the tank turn rate to
   *  match the sim's weighted body turn, so the client heading never drifts ahead
   *  of what the body can achieve. Optional — defaults to 0 (fastest turn). */
  speed?: number;
}

export function neutralContext(): InputContext {
  // Broadcast default: W moves toward -Z (into the pitch), D toward +X.
  return { forwardX: 0, forwardZ: -1, rightX: 1, rightZ: 0, possessing: false, speed: 0 };
}

/**
 * DOM-free raw input state. All mutation goes through the `feed*` methods so the
 * DOM layer and the test harness share exactly one code path.
 */
export class InputState {
  readonly keys = new Set<string>();

  aimYaw = 0;
  liftBias = 0;
  /**
   * Camera-look yaw (§1/§2, 2026-07-18). The camera's own heading, driven ONLY
   * by the mouse and NEVER by movement — this is what decouples the camera from
   * WASD. `aimYaw` (above) is the body/kick aim and still snaps to the run
   * heading each frame in buildInputCommand; feeding THAT to the follow-cams is
   * why WASD used to drag the camera and the mouse "couldn't rotate it away".
   * The follow-cams read this instead — and, since the crosshair became the aim
   * (game/crosshairAim.ts), so does the strike heading in every camera the mouse
   * can turn. It therefore keeps moving DURING a charge (it used to freeze; see
   * feedMouseMove for why that had to go), just at the finer aim gain.
   */
  cameraLookYaw = 0;
  /** Seeded from the controlled player's facing the first time the loop asks,
   *  and re-seeded on a player switch, so the orbit starts behind the player. */
  cameraLookInit = false;
  /**
   * Mouse feel, live-tunable from the settings screen (§7.1). `mouseSensitivity`
   * turns free look; `aimSensitivity` turns the view (and therefore the strike
   * heading — the crosshair IS the aim) while a shot/pass CHARGES, plus the
   * legacy `aimYaw` path the fixed-azimuth rigs still use. Two knobs by design —
   * fast look with precise aim (or the reverse) is a common preference, and it
   * is the same split every shooter ships as hipfire-vs-ADS sensitivity. Both
   * default to the recovered reference
   * (DEFAULT_*_SENSITIVITY); InputManager.setSensitivity overwrites them from
   * persisted settings on match start and on every live slider edit.
   */
  mouseSensitivity = DEFAULT_MOUSE_SENSITIVITY;
  aimSensitivity = DEFAULT_AIM_SENSITIVITY;
  /**
   * Tank-control heading (radians, sim yaw convention atan2(z,x)). A/D ROTATE
   * this; W accelerates along it; S brakes/backpedals along its reverse. The
   * mouse only softly influences it while running and never overwrites A/D. It is
   * the sole source of the human's `moveX/moveZ` and (when not aiming) `aimYaw`,
   * so the wire format is unchanged — only how the client fills it. Seeded from
   * the body facing, and re-seeded on a §4.8 switch, so it never starts stale.
   */
  moveHeading = 0;
  moveHeadingInit = false;
  /** Live curl accumulator, [-1, 1]. */
  curl = 0;
  /** Set on a shoot/pass button-DOWN: the first aiming tick snaps aimYaw to the
   *  current RUN direction so charging never flips the body toward a stale mouse
   *  aim (§7 — "RMB makes the player rotate the opposite way"). The mouse then
   *  adjusts it live from there (§3). */
  aimInitFromRun = false;

  private shootHeld = false;
  private passHeld = false;
  private shootStartMs = 0;
  private passStartMs = 0;

  // One-shot latches, consumed by buildInputCommand.
  private pendingFlags = 0;
  /** A6 §9 — the aim held at the shoot/pass RELEASE edge, latched with the flag.
   *  The release event clears shootHeld/passHeld in the same breath, so by the
   *  time buildInputCommand runs, isAiming() is already false and aimYaw would
   *  fall back to the RUN heading — a sprint shot fired along the run line, not
   *  the arrow. The stored aim at release is authoritative. NaN = none pending. */
  private pendingAimYaw = Number.NaN;
  private pendingShootCharge = 0;
  private pendingPassCharge = 0;
  private pendingShootCurl = 0;

  // ── Strike input buffer (§4.2) ────────────────────────────────────────────
  // Review: "when i am running and holding the left click and releasing its not
  // kicking the ball". It was total, not occasional, and the cause is geometric.
  //
  // A dribble knock deliberately pushes the ball ahead in a push-and-chase
  // rhythm, so while sprinting the ball oscillates between ~0.695 m (the distance
  // at which the next knock fires) and ~0.835 m. A strike, however, requires the
  // ball inside BALL_PLAY_REACH (0.7 m) — measured, the ball is out of reach for
  // 22 of every 23 ticks at sprint. Releasing the button on any of those 22 ticks
  // dropped the request on the floor and nothing happened.
  //
  // The fix is NOT to widen the strike gate. That gate is contact truth: it was
  // introduced because shots were leaving a boot 1.2 m from the ball, which is
  // exactly what "the ball moves before it was touched" looks like, and
  // contact-truth.test.ts pins it. Widening it makes the ball fly off untouched
  // again — I tried it, and that suite correctly went red.
  //
  // So the REQUEST is buffered instead, and the strike fires on the next touch —
  // which is what every football game does and what a player actually intends.
  // The buffer is held here, on the client, because it is a fact about the human's
  // input, not about the world: no sim change, no WorldState field, no wire
  // change, and the strike still resolves only when the boot is genuinely on the
  // ball.
  private strikeBufferTicks = 0;
  private strikeBufferFlags = 0;
  private bufferedShootCharge = 0;
  private bufferedPassCharge = 0;
  private bufferedShootCurl = 0;
  private bufferedAimYaw = Number.NaN;
  private pendingSlideOrFirst = false;
  private pendingTactical: TacticalCommand = TacticalCommand.None;
  /** Latched skill move (0 = none) awaiting emission. */
  private pendingSkill: SkillMove = SkillMove.None;

  /** True only while F+direction should keep re-triggering suppressed. */
  private skillLatched = false;

  /** When the jump key last went DOWN (ms clock of the feed calls); -1 = never.
   *  Read by the bicycle-kick compose on shoot release. */
  private lastJumpDownMs = -1;
  /**
   * THE BOOST LATCH. 0 off, 1 armed, 2 armed harder.
   *
   * A latch and not a held key: the tiers are priced in whole seconds of stamina
   * (60 / 30 / 15 s of bar), and holding a key down for a minute is not a
   * control. Public because `buildInputCommand` is a free function.
   */
  boostTier = 0;
  private lastBoostTapMs = -1;

  // App-level edges (not sim input): consumed by the loop / UI.
  private cameraCyclePending = false;
  private menuPending = false;

  // §5 middle-mouse team commands. An internal tick clock (advanced by
  // buildInputCommand) times the single/double window — mouse events run BETWEEN
  // ticks, so ~16 ms granularity is plenty for a 300 ms window and keeps timing on
  // one monotonic clock rather than mixing in the DOM event clock.
  private middleClockMs = 0;
  /** Clock time of a lone first middle-click awaiting its double window; -1 = none. */
  private middlePendingSingleAtMs = -1;
  /** Ignore middle-clicks until this clock time (post-command cooldown). */
  private middleCooldownUntilMs = 0;
  /** One-shot HUD string for the last resolved middle command ('' = none). */
  private middleFeedback = '';
  /** One-shot: a SINGLE-click circulate resolved this frame (the loop turns it into
   *  a safe pass when the human is carrying). */
  private pendingCirculate = false;

  isShootHeld(): boolean {
    return this.shootHeld;
  }

  /** True while a pass is charging — drives the pass-target indicator (3C). */
  isPassHeld(): boolean {
    return this.passHeld;
  }

  /**
   * Is the player deliberately aiming, rather than just running? True while a
   * shot or pass is being charged — the moments where the mouse is choosing a
   * direction and the body must hold it instead of snapping to the run.
   * Facing falls back to the run direction whenever this is false; see the
   * note in buildInputCommand.
   */
  isAiming(): boolean {
    return this.shootHeld || this.passHeld;
  }

  isRadarHeld(map: ControlMap): boolean {
    return this.actionHeld(map, 'radar');
  }

  /**
   * REQUESTED power fraction (0..1) for the charge currently being held — the
   * SAME interpolation the sim uses to turn a charge duration into shot/pass
   * speed (`clamp01((held - min) / (max - min))`, see ball.ts buildShot /
   * buildPass and their `t`). 0 whenever that action is not currently held.
   *
   * The caller supplies the real SHOT_/PASS_CHARGE_MIN_S/MAX_S from
   * @fg/shared/sim's constants.ts — this file is client-only input feel and
   * must never hardcode a sim tunable (README's one lint-adjacent convention).
   * Read by the HUD power meter (ui/hud.ts) and the aim-arrow indicator's
   * charge-intensity feed, so a charging shot/pass shows the POWER that would
   * be requested if released now, not the ball's eventual speed.
   */
  chargeFraction(kind: 'shoot' | 'pass', minS: number, maxS: number, nowMs: number): number {
    const held = kind === 'shoot' ? this.shootHeld : this.passHeld;
    if (!held) return 0;
    const startMs = kind === 'shoot' ? this.shootStartMs : this.passStartMs;
    const heldS = (nowMs - startMs) / 1000;
    const span = maxS - minS;
    if (span <= 0) return heldS >= minS ? 1 : 0;
    return clamp((heldS - minS) / span, 0, 1);
  }

  actionHeld(map: ControlMap, action: BindableAction): boolean {
    for (const code of this.keys) {
      if (map.keys[code] === action) return true;
    }
    return false;
  }

  // ── Feed: keyboard ────────────────────────────────────────────────────────
  feedKeyDown(code: string, map: ControlMap, nowMs = 0): void {
    if (this.keys.has(code)) return; // ignore auto-repeat
    const skillHeld = this.actionHeld(map, 'skillModifier');
    this.keys.add(code);
    const action = map.keys[code];
    if (!action) return;
    switch (action) {

      case 'boost':
        // Tap to arm tier 1; tap AGAIN inside the window to reach tier 2; a tap
        // after that turns it off. Same double-tap window the middle-mouse team
        // command already uses, so the two feel alike under the hand.
        if (this.boostTier > 0 && nowMs - this.lastBoostTapMs <= MIDDLE_DOUBLE_WINDOW_MS) {
          this.boostTier = Math.min(2, this.boostTier + 1);
        } else {
          this.boostTier = this.boostTier > 0 ? 0 : 1;
        }
        this.lastBoostTapMs = nowMs;
        break;
      case 'jump':
        // Edge-triggered: the sim's jump is a grounded-only action, and a
        // one-shot avoids re-jump-on-landing while the key is held. Auto-repeat
        // never reaches here (guard above).
        this.pendingFlags |= InputFlags.Jump;
        this.lastJumpDownMs = nowMs;
        break;
      case 'standingOrJockey':
        // Press edge = a standing-tackle poke; holding it = jockey (added live).
        this.pendingFlags |= InputFlags.StandingTackle;
        break;
      case 'slideOrFirstTouch':
        this.pendingSlideOrFirst = true;
        break;
      case 'punch':
        this.pendingFlags |= InputFlags.PunchPlayer;
        break;
      case 'kick':
        this.pendingFlags |= InputFlags.KickPlayer;
        break;
      case 'tactic1':
        this.pendingTactical = TacticalCommand.PressHigh;
        break;
      case 'tactic2':
        this.pendingTactical = TacticalCommand.DropDeep;
        break;
      case 'tactic3':
        this.pendingTactical = TacticalCommand.OffsideTrap;
        break;
      case 'tactic4':
        this.pendingTactical = TacticalCommand.ManMark;
        break;
      case 'cameraCycle':
        this.cameraCyclePending = true;
        break;
      case 'menu':
        this.menuPending = true;
        break;
      case 'shoot':
        // Charge start (§4.1). Whatever code drives 'shoot' — mouse button or,
        // if rebound, a keyboard key — this is the ONE place charging begins.
        this.shootHeld = true;
        this.shootStartMs = nowMs;
        this.curl = 0; // fresh curve per shot
        this.aimInitFromRun = true; // start the aim from the run, not a stale mouse yaw (§7)
        break;
      case 'pass':
        this.passHeld = true;
        this.passStartMs = nowMs;
        this.curl = 0; // fresh curve per pass (matches shoot)
        this.aimInitFromRun = true; // (§7)
        break;
      case 'callForPass':
        this.pendingFlags |= InputFlags.CallForPass;
        break;
      case 'moveForward':
      case 'moveBack':
      case 'moveLeft':
      case 'moveRight':
        // A direction pressed while the skill modifier is held arms a skill
        // move; SPRINT held as well arms the advanced variant of that
        // direction (knock-and-run at pace, step-over/elastico beats a man on
        // the run, rabona fake sells hardest at speed). Previously the four
        // advanced SkillMove members existed in the sim but no input could
        // ever produce them.
        if (skillHeld && !this.skillLatched) {
          const sprintHeld = this.actionHeld(map, 'sprint');
          this.pendingSkill = skillForDirection(action, sprintHeld);
          this.skillLatched = true;
        }
        break;
      default:
        break;
    }
  }

  /** `nowMs` is only consulted by the shoot/pass release-charge computation;
   *  every other action ignores it, so keyboard call sites that don't care
   *  about timing may omit it. */
  feedKeyUp(code: string, map: ControlMap, nowMs = 0): void {
    this.keys.delete(code);
    const action = map.keys[code];
    if (!action) return;
    switch (action) {
      case 'skillModifier':
        this.skillLatched = false;
        break;
      case 'shoot':
        if (!this.shootHeld) break; // no matching press (e.g. rebound mid-hold)
        this.shootHeld = false;
        this.pendingFlags |= InputFlags.Shoot;
        // House controls v2: B(hold) + jump(recent) + shoot release = bicycle
        // kick REQUEST. Composed here, where both the modifier state and the
        // release time are known; the sim validates legality (types.ts).
        if (
          bicycleComposeActive(
            this.actionHeld(map, 'bicycleModifier'),
            this.actionHeld(map, 'jump'),
            this.lastJumpDownMs,
            nowMs,
          )
        ) {
          this.pendingFlags |= InputFlags.BicycleKick;
        }
        // Q/R (hold) + shoot release = LOFT the strike over the keeper
        // (owner: "so the ball goes from above"). Same composition rule as
        // the pass below: onto THIS release event only, never a bare held
        // flag - see loftComposeActive's header.
        if (loftComposeActive(this.actionHeld(map, 'loftModifier'))) {
          this.pendingFlags |= InputFlags.ThroughBall;
        }
        this.pendingShootCharge = (nowMs - this.shootStartMs) / 1000;
        this.pendingShootCurl = this.curl;
        this.pendingAimYaw = this.aimYaw; // A6 §9: the arrow's aim AT release rides the flag
        break;
      case 'pass':
        if (!this.passHeld) break;
        this.passHeld = false;
        this.pendingFlags |= InputFlags.Pass;
        // House controls v3 (§6): R(hold) + pass release = loft/cross REQUEST
        // — composed onto this SAME Pass event only (see loftComposeActive's
        // header for why it must never be a bare held flag).
        if (loftComposeActive(this.actionHeld(map, 'loftModifier'))) {
          this.pendingFlags |= InputFlags.ThroughBall;
        }
        this.pendingPassCharge = (nowMs - this.passStartMs) / 1000;
        this.pendingAimYaw = this.aimYaw; // A6 §9: the arrow's aim AT release rides the flag
        break;
      default:
        break;
    }
  }

  // ── Feed: mouse ───────────────────────────────────────────────────────────
  /**
   * A mouse button is just another code (defects #11/#12): translate
   * `MouseEvent.button` to its synthetic `MouseN` code and hand off to the
   * identical keyboard path — same held-state Set, same action switch, same
   * charge/curl/bicycle-kick mechanics. Whether LMB fires Shoot, RMB fires
   * Pass, or the player rebound them onto other codes is entirely a
   * `ControlMap` lookup that `feedKeyDown`/`feedKeyUp` already perform.
   */
  feedMouseDown(button: number, map: ControlMap, nowMs: number): void {
    // §5: the MIDDLE button is the team-command gesture (single = circulate,
    // double = pass-to-me) — a timed single/double, which does not fit the
    // instantaneous code→action model, so it is handled here and NOT routed to a
    // bindable action.
    if (button === 1) {
      this.middleButtonDown();
      return;
    }
    this.feedKeyDown(mouseCode(button), map, nowMs);
  }

  feedMouseUp(button: number, map: ControlMap, nowMs: number): void {
    if (button === 1) return; // §5 middle button has no held/up semantics
    this.feedKeyUp(mouseCode(button), map, nowMs);
  }

  /**
   * §5: a middle-mouse press. The FIRST press opens the double-click window; a
   * SECOND press inside it is a DOUBLE = pass-to-me (CallForPass, emitted now).
   * The window lapsing with no second press is a SINGLE = team circulate (resolved
   * in advanceMiddle). Never fires while a shot/pass trajectory is being drawn, and
   * honours a post-command cooldown. Touches no camera state.
   */
  private middleButtonDown(): void {
    if (this.shootHeld || this.passHeld) return; // don't fire while drawing the trajectory
    if (this.middleClockMs < this.middleCooldownUntilMs) return; // cooldown
    if (this.middlePendingSingleAtMs >= 0) {
      // Second click inside the window → DOUBLE.
      this.middlePendingSingleAtMs = -1;
      this.pendingFlags |= InputFlags.CallForPass;
      this.middleFeedback = 'PASS TO ME';
      this.middleCooldownUntilMs = this.middleClockMs + MIDDLE_COMMAND_COOLDOWN_MS;
    } else {
      this.middlePendingSingleAtMs = this.middleClockMs;
    }
  }

  /** Advance the middle-click clock and resolve a lapsed single-click into a team
   *  CIRCULATE. Called once per built input so the window is measured on the sim's
   *  own cadence. */
  advanceMiddle(dtMs: number): void {
    this.middleClockMs += dtMs;
    if (
      this.middlePendingSingleAtMs >= 0 &&
      this.middleClockMs - this.middlePendingSingleAtMs >= MIDDLE_DOUBLE_WINDOW_MS
    ) {
      this.middlePendingSingleAtMs = -1;
      this.pendingCirculate = true;
      this.pendingTactical = TacticalCommand.CirculatePass;
      this.middleFeedback = 'CIRCULATE';
      this.middleCooldownUntilMs = this.middleClockMs + MIDDLE_COMMAND_COOLDOWN_MS;
    }
  }

  /** Consume the one-shot HUD string for the last resolved middle command. */
  consumeMiddleFeedback(): string {
    const v = this.middleFeedback;
    this.middleFeedback = '';
    return v;
  }

  /** Consume the one-shot single-click circulate flag (true once, on resolve). */
  consumeCirculate(): boolean {
    const v = this.pendingCirculate;
    this.pendingCirculate = false;
    return v;
  }

  /** Pointer-lock relative motion. dx > 0 = mouse moved right. */
  feedMouseMove(dx: number, _dy: number): void {
    // Aim is ALWAYS live: the mouse steers the shot/pass direction continuously,
    // INCLUDING while charging. Routing charge-time motion to curl only (the old
    // behaviour) froze the aim mid-hold — the arrow, the kick direction and the
    // pass-target scoring all stopped updating the instant LMB went down. (§3)
    //
    // `aimYaw` is now the LEGACY aim path: it is what still steers the strike in
    // the fixed-azimuth rigs (Broadcast/Tele/Dynamic — cameraAimsWithCrosshair),
    // where a screen-centre reticle cannot aim. In every camera the crosshair DOES
    // aim, game/crosshairAim.ts overwrites cmd.aimYaw with the camera heading
    // below, so this accumulator is harmless there rather than a second opinion.
    this.aimYaw = wrapAngle(this.aimYaw + dx * this.aimSensitivity);
    const charging = this.shootHeld || this.passHeld;
    if (charging) {
      // DRAWING A CURVE (§8/§9): the lateral sweep bends the ball. The sim scales
      // curl gently (MOUSE_CURL_SIDESPIN), so ordinary aiming adds a subtle bend
      // and a deliberate flick a visible one. (§4)
      this.curl = clamp(this.curl + dx * CURL_GAIN, -1, 1);
    }
    // FREE LOOK (§1/§2): the mouse orbits the CAMERA, never the movement. This is
    // the whole decoupling — WASD can never write cameraLookYaw, so it can never
    // drag the camera, and the look persists across a run.
    //
    // ⚠ THE CAMERA NO LONGER FREEZES WHILE CHARGING, and that override is
    // deliberate (2026-07-25). The freeze was §9's "camera stable while drawing
    // the trajectory" / §10's trajectory freeze, written when the aim was a
    // SEPARATE number with its own on-pitch arrow: freezing the view then cost
    // nothing, because the arrow kept steering underneath it. Now that the strike
    // goes down the camera's own forward ray (game/crosshairAim.ts — the owner's
    // *"use the cross to decide where the ball will go"*), freezing the camera
    // freezes the AIM: you could point, hold and release, but a mid-charge
    // correction — which every player makes, and which worked before the rewire —
    // became impossible. A stable view is worth less than an aim you can adjust.
    //
    // What survives of the old intent: the charge turns at `aimSensitivity`
    // rather than `mouseSensitivity`, so the view still steadies while you draw a
    // shot (it turns at the finer aim gain, 0.0014 vs 0.0018 by default, and the
    // player can take it lower still). That is also what keeps the aim slider a
    // LIVE control — with one heading instead of two, "precision aim gain" is the
    // only honest meaning it has left. See ui/settings.ts CameraSettings.
    const gain = charging ? this.aimSensitivity : this.mouseSensitivity;
    this.cameraLookYaw = wrapAngle(this.cameraLookYaw + dx * gain);
  }

  /**
   * Seed the camera-look yaw from the controlled player's facing. The loop calls
   * this the first time it needs a camera yaw and again on a §4.8 player switch,
   * so the orbit always starts behind the player rather than snapping from a
   * stale heading. Idempotent per (re)seed via `cameraLookInit`.
   */
  seedCameraLook(yaw: number): void {
    this.cameraLookYaw = yaw;
    this.cameraLookInit = true;
  }

  /** Seed the tank heading from the controlled player's facing (first frame and
   *  on a §4.8 switch), so A/D rotate from where the body already points. */
  seedMoveHeading(yaw: number): void {
    this.moveHeading = yaw;
    this.moveHeadingInit = true;
  }

  feedWheel(deltaY: number): void {
    // Wheel up (deltaY < 0) biases the shot higher.
    this.liftBias = clamp(this.liftBias - Math.sign(deltaY) * WHEEL_LIFT_STEP, -1, 1);
  }

  // ── App-level edge consumers ────────────────────────────────────────────────
  consumeCameraCycle(): boolean {
    const v = this.cameraCyclePending;
    this.cameraCyclePending = false;
    return v;
  }

  consumeMenu(): boolean {
    const v = this.menuPending;
    this.menuPending = false;
    return v;
  }

  // ── Internal accessors for buildInputCommand ────────────────────────────────
  takeOneShots(possessing: boolean): {
    flags: number;
    shootCharge: number;
    passCharge: number;
    shootCurl: number;
    /** A6 §9 — aim latched at the shoot/pass release edge (NaN = none). */
    aimYaw: number;
    skill: SkillMove;
    tactical: TacticalCommand;
  } {
    let flags = this.pendingFlags;
    if (this.pendingSlideOrFirst) {
      // E: first-touch when you have the ball, slide tackle when you don't (§4.1).
      flags |= possessing ? InputFlags.FirstTouch : InputFlags.SlideTackle;
    }

    // ── Strike buffer: re-assert an unserved shot/pass until the next touch ──
    // ARM only while he actually has the ball: a strike thrown at a loose or
    // opposition ball is a one-shot attempt at THIS moment, and re-asserting it
    // would keep swinging at a ball that has moved on.
    const strikeNow = flags & (InputFlags.Shoot | InputFlags.Pass);
    if (strikeNow !== 0 && possessing) {
      this.strikeBufferFlags = strikeNow;
      this.strikeBufferTicks = STRIKE_BUFFER_TICKS;
    } else if (this.strikeBufferTicks > 0) {
      if (!possessing) {
        // The ball has left him — served (or lost). Either way, stop asking.
        // This is what makes a double-kick impossible: a struck ball is no longer
        // his on the very next tick, so the buffer cannot survive to re-fire.
        this.strikeBufferTicks = 0;
        this.strikeBufferFlags = 0;
      } else {
        this.strikeBufferTicks--;
        flags |= this.strikeBufferFlags;
        // The charge must ride along, or the re-asserted strike arrives with 0
        // power and the sim rejects it below SHOT_CHARGE_MIN_S — the same silent
        // no-op wearing a different hat.
        if ((this.strikeBufferFlags & InputFlags.Shoot) !== 0) {
          this.pendingShootCharge = this.bufferedShootCharge;
          this.pendingShootCurl = this.bufferedShootCurl;
        }
        if ((this.strikeBufferFlags & InputFlags.Pass) !== 0) {
          this.pendingPassCharge = this.bufferedPassCharge;
        }
        this.pendingAimYaw = this.bufferedAimYaw;
      }
    }
    if (strikeNow !== 0 && possessing) {
      // Snapshot what the release asked for, so each re-assertion is identical to
      // the original intent rather than a decayed copy of itself.
      this.bufferedShootCharge = this.pendingShootCharge;
      this.bufferedPassCharge = this.pendingPassCharge;
      this.bufferedShootCurl = this.pendingShootCurl;
      this.bufferedAimYaw = this.pendingAimYaw;
    }

    const out = {
      flags,
      shootCharge: this.pendingShootCharge,
      passCharge: this.pendingPassCharge,
      shootCurl: this.pendingShootCurl,
      aimYaw: this.pendingAimYaw,
      skill: this.pendingSkill,
      tactical: this.pendingTactical,
    };
    this.pendingFlags = 0;
    this.pendingShootCharge = 0;
    this.pendingPassCharge = 0;
    this.pendingShootCurl = 0;
    this.pendingAimYaw = Number.NaN;
    this.pendingSlideOrFirst = false;
    this.pendingSkill = SkillMove.None;
    this.pendingTactical = TacticalCommand.None;
    return out;
  }

  decayCurl(dtMs: number): void {
    if (this.shootHeld || this.passHeld) return; // still charging — hold the value
    const k = dtMs <= 0 ? 0 : Math.min(1, dtMs / CURL_DECAY_TIME_MS);
    this.curl += (0 - this.curl) * k;
    if (Math.abs(this.curl) < 1e-3) this.curl = 0;
  }
}

/** Map a movement direction pressed under the skill modifier to a skill move. (§4.6) */
function skillForDirection(action: BindableAction, sprint = false): SkillMove {
  switch (action) {
    case 'moveForward':
      return sprint ? SkillMove.KnockAndRun : SkillMove.BallRoll;
    case 'moveBack':
      return sprint ? SkillMove.RabonaFakeShot : SkillMove.DragBack;
    case 'moveLeft':
      return sprint ? SkillMove.StepOver : SkillMove.BodyFeintLeft;
    case 'moveRight':
      return sprint ? SkillMove.Elastico : SkillMove.BodyFeintRight;
    default:
      return SkillMove.None;
  }
}

/**
 * Pure mapping: current raw state + control map + camera/possession context ->
 * one InputCommand. Consumes the one-shot latches and advances curl decay, so it
 * must be called exactly once per produced input.
 */
export function buildInputCommand(
  state: InputState,
  map: ControlMap,
  ctx: InputContext,
  seq: number,
  tick: number,
  dtMs: number,
): InputCommand {
  const cmd = emptyInput(seq, tick);

  /*
   * CAMERA-RELATIVE movement (modern 3rd-person), replacing tank steering. WASD is
   * a DIRECTION in the camera's ground plane, NOT a heading to rotate:
   *   W → camera-forward   S → camera-back   A → camera-left   D → camera-right
   * so W+D is a diagonal, and A/D strafe rather than spin the player in place. The
   * camera basis (ctx.forwardX/forwardZ and ctx.rightX/rightZ, unit ground
   * vectors) is filled by camera.movementBasis() in the loop each frame.
   *
   * The wire contract is unchanged: moveX/moveZ are still "a desired world heading
   * + magnitude" (here a unit vector, magnitude 1 while any WASD is held) and
   * aimYaw is still the body/kick facing — so the sim's weighted momentum model
   * (heading + body rotate toward the target at the speed-scaled turn rate) and the
   * AI/netcode are untouched; only how the CLIENT fills them changed. The sim does
   * ALL the turning, so the client never rotates the player — it just points where
   * WASD wants to go, and the body smoothly follows.
   */
  const fwdIn = (state.actionHeld(map, 'moveForward') ? 1 : 0) - (state.actionHeld(map, 'moveBack') ? 1 : 0);
  const rightIn = (state.actionHeld(map, 'moveRight') ? 1 : 0) - (state.actionHeld(map, 'moveLeft') ? 1 : 0);
  let mx = ctx.forwardX * fwdIn + ctx.rightX * rightIn;
  let mz = ctx.forwardZ * fwdIn + ctx.rightZ * rightIn;
  const mlen = Math.hypot(mx, mz);
  const hasMove = mlen > 1e-6;
  if (hasMove) { mx /= mlen; mz /= mlen; } // normalize — a diagonal is not faster

  // ⚠ WALK. `moveX/moveZ` is a heading AND a throttle: the sim reads its
  // magnitude as `intent.moveMag` and multiplies the speed cap by it, with the
  // explicit note that "a half-pressed stick must stay a half-pressed stick or
  // every gamepad user walks at sprint pace". A keyboard cannot half-press, so
  // it always asked for 1.0 and the human player had exactly THREE speeds:
  // standing, jog, sprint. Measured over a goal-to-goal traverse, 3 samples of
  // 1235 landed in the Walk band [1.20, 2.00] m/s — 0.2 % of the time spent
  // moving — so the walk cycle was in the pack and never on the screen.
  //
  // Holding the walk key scales the vector instead of the cap, which is the same
  // lever a stick pulls and needs nothing in the sim.
  //
  // ⚠ THE THROTTLE IS A FRACTION OF THE SPEED CAP, AND THE CAP IS 6.0, NOT 2.5.
  // First attempt used 0.62 on the assumption the cap was the ~2.5 m/s seen
  // while dribbling. Without the ball `maxSpeedFor` returns MAX_SPEED_RUN, so
  // 0.62 gave 3.72 m/s — measured in play, and still a run. The number wanted is
  //
  //     NATURAL_SPEED.Walk / MAX_SPEED_RUN  =  1.549 / 6.0  =  0.258
  //
  // which lands mid-Walk-band (1.20-2.00) at M_Walk's own natural speed, where
  // its playback rate is 1.00 and its feet do not slide. WITH the ball the cap is
  // lower, so the same fraction gives a slower walk and the ladder drops to
  // SlowWalkForward — which is correct: walking with the ball at your feet IS
  // slower, and that clip exists for it.
  const WALK_THROTTLE = 0.258;

  // Facing memory: hold the LAST movement direction while stationary, so a player
  // standing still keeps his facing instead of snapping to a default. Seeded from
  // the body facing on the very first frame (and re-seeded on a §4.8 switch).
  if (!state.moveHeadingInit) { state.moveHeading = state.aimYaw; state.moveHeadingInit = true; }

  // BACKPEDAL (filmed defect: "KeyS turns the player 180° and runs at the
  // camera"). A movement request within TURN_COMMIT of the current facing
  // turns the body as before — that is ordinary steering. A request FURTHER
  // off than that (S, or a hard back-diagonal) keeps the facing where it is
  // and lets the sim's off-facing locomotion take over: Backpedal/Strafe
  // anims, off-facing speed cap. Holding SPRINT commits the turn instead —
  // that is the deliberate "turn around and run" escape hatch, so nothing
  // about forward navigation is lost.
  //
  // This decision is made BEFORE the throttle because the throttle depends on
  // it; scaling the vector uniformly cannot change its angle, so reading `want`
  // off the un-throttled vector is exact.
  let commitsTurn = true;
  if (hasMove) {
    const want = Math.atan2(mz, mx);
    let diff = want - state.moveHeading;
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;
    // A carrier shields the ball and holds his facing; a player with nothing at
    // his feet turns and goes. See FREE_TURN_COMMIT_RAD.
    const cone = ctx.possessing ? BACKPEDAL_TURN_COMMIT_RAD : FREE_TURN_COMMIT_RAD;
    commitsTurn = state.actionHeld(map, 'sprint') || Math.abs(diff) <= cone;
    if (commitsTurn) state.moveHeading = want;
  }

  // ⚠ THE WALK KEY IS A FORWARD-GAIT MODIFIER, NOT A GLOBAL SPEED DIAL.
  // Throttling an OFF-FACING request is a moon-walk, and it was one: measured in
  // play, walking backwards ran at 0.258 x 2.70 = 0.696 m/s against
  // `NATURAL_SPEED.Backpedal` of 3.03, so the playback rate wanted 0.230 and the
  // clamp floor of 0.65 held it at 1.97 m/s of ground sweep — 1.27 m/s of pure
  // foot slide, the exact defect the walk key was added to remove.
  //
  // There is no slow backpedal or slow strafe clip to drop to (the forward
  // ladder has `SlowWalkForward`; the off-facing gaits are single clips), and a
  // band whose speed sits below its own clip's floor is asking the rate clamp to
  // cover for it — the same finding `RATE_MAX` carries for the sprint. So the
  // throttle applies only where a forward gait will serve the request. Left
  // alone, the sim's own off-facing cap gives 2.70 m/s: rate 0.891 on Backpedal,
  // 1.000 on StrafeLeft and 1.256 on StrafeRight, all comfortably inside
  // [0.65, 1.45] and none of them sliding.
  const walking = hasMove && commitsTurn && state.actionHeld(map, 'walk');
  if (walking) { mx *= WALK_THROTTLE; mz *= WALK_THROTTLE; }
  cmd.moveX = hasMove ? mx : 0;
  cmd.moveZ = hasMove ? mz : 0;

  // Facing target: while AIMING (charging a shot/pass, or jockeying) the mouse aims
  // and the body holds that; otherwise the body faces the MOVEMENT direction (or
  // its last held direction when stationary). The mouse's free-look never writes
  // the facing here, so a jittery mouse can never spin the player (§7 decouple) —
  // and the sim turns the body toward this target smoothly, never snapping it.
  const aiming = state.isAiming() || state.actionHeld(map, 'standingOrJockey');
  if (aiming) {
    if (state.aimInitFromRun) { state.aimYaw = state.moveHeading; state.aimInitFromRun = false; }
    cmd.aimYaw = state.aimYaw;
  } else {
    state.aimInitFromRun = false;
    cmd.aimYaw = state.moveHeading;
  }
  cmd.liftBias = state.liftBias;

  // Held flags.
  let flags = 0;
  if (state.actionHeld(map, 'sprint')) flags |= InputFlags.Sprint;
  // ── THE STAMINA BOOST — A LATCH, NOT A HOLD ─────────────────────────────
  // Owner: "when the user press button 1 the stamina gets activates ... if 1 is
  // pressed twiced the stamina will burn even with higher rate". So it TOGGLES:
  // tap to arm tier 1, tap again within BOOST_DOUBLE_TAP_MS for tier 2, and a
  // tap after that window turns it off. Holding a key for a minute is not a
  // control, and the burn rates only make sense as a state you choose to be in.
  //
  // Tier 2 sets BOTH bits so a reader that only knows Boost1 still sees a boost.
  if (state.boostTier >= 1) flags |= InputFlags.Boost1;
  if (state.boostTier >= 2) flags |= InputFlags.Boost2;
  if (state.actionHeld(map, 'standingOrJockey')) flags |= InputFlags.Jockey;
  // Close control (Ctrl) — tight dribble + jogging pace, which is also what
  // lets a keyboard player stop draining stamina (see CLOSE_CONTROL_SPEED).
  // Ctrl. `skillModifier` IS Ctrl in the house scheme, and reading it here
  // rather than adding a second physical binding is what makes Ctrl do what
  // the owner asked without disturbing the skill-move layer: with a direction
  // it is still a skill move, on its own it is close control.
  if (state.actionHeld(map, 'skillModifier')) {
    flags |= InputFlags.CloseControl;
  }
  // ── KEEPER VERBS RIDE THE EXISTING KEYS ──────────────────────────────────
  //
  // Owner's scheme: C dives left, V dives right, X holds the ball. Those keys
  // are already switchPlayer / cameraCycle / callForPass, and rebinding them
  // globally broke four control-map acceptance tests for no gain — because the
  // conflict is imaginary. `goalkeeper.ts` is the ONLY reader of these flags
  // and it runs for the GK slot alone, so an outfield player pressing C still
  // switches player and simply carries a bit nobody looks at. When you ARE the
  // keeper, C means dive.
  //
  // The one real overlap is X: as an outfield player it calls for a pass, as
  // the keeper it claims the ball. Both are "the X button does the useful
  // thing here", which is what a context key is.
  if (state.actionHeld(map, 'radar')) flags |= InputFlags.GkDiveLeft;
  if (state.actionHeld(map, 'cameraCycle')) flags |= InputFlags.GkDiveRight;
  if (state.actionHeld(map, 'callForPass')) flags |= InputFlags.GkHold;
  if (state.actionHeld(map, 'skillModifier')) flags |= InputFlags.SkillModifier;

  // §5: advance the middle-mouse window and resolve a lapsed single-click into a
  // circulate tactical BEFORE the one-shots are drained, so it rides this tick.
  state.advanceMiddle(dtMs);

  // One-shot flags/charges (edge-latched).
  const once = state.takeOneShots(ctx.possessing);
  flags |= once.flags;
  cmd.flags = flags;
  // A6 §9: the aim STORED at the release edge is authoritative for the kick it
  // released. The release event cleared shootHeld/passHeld before this frame's
  // command was built, so isAiming() above already fell back to the RUN heading
  // — without this override a sprint shot fires along the run line, not the
  // displayed arrow. Never recalculated from later body rotation.
  if ((once.flags & (InputFlags.Shoot | InputFlags.Pass)) !== 0 && Number.isFinite(once.aimYaw)) {
    cmd.aimYaw = once.aimYaw;
  }
  cmd.shootCharge = once.shootCharge;
  cmd.passCharge = once.passCharge;
  cmd.skill = once.skill;
  cmd.tactical = once.tactical;

  // Curl: the just-released shot carries its captured curl; otherwise the live
  // (decaying) value, which is ~0 outside a charge.
  cmd.curl = (once.flags & InputFlags.Shoot) !== 0 ? clamp(once.shootCurl, -1, 1) : clamp(state.curl, -1, 1);

  state.decayCurl(dtMs);
  return cmd;
}

// ---------------------------------------------------------------------------
// DOM wiring
// ---------------------------------------------------------------------------

/**
 * Attaches real listeners to a canvas/element and manages pointer lock. During
 * play the pointer is locked so mouse motion feeds aim/curl; Escape releases it.
 */
export class InputManager {
  readonly state = new InputState();
  private map: ControlMap;
  private readonly target: HTMLElement;
  private attached = false;
  private readonly now: () => number;

  constructor(target: HTMLElement, map: ControlMap, now: () => number = () => performance.now()) {
    this.target = target;
    this.map = map;
    this.now = now;
  }

  setMap(map: ControlMap): void {
    this.map = map;
  }

  /**
   * Apply the persisted mouse-feel settings live (§7.1). Called once on match
   * start (from the persisted GameSettings) and again on every settings-screen
   * slider edit, so the change is felt the same frame. Clamped to a sane band so
   * a corrupt or hand-edited blob can never render the mouse unusable.
   */
  setSensitivity(mouse: number, aim: number): void {
    if (Number.isFinite(mouse)) this.state.mouseSensitivity = clamp(mouse, 0.0002, 0.02);
    if (Number.isFinite(aim)) this.state.aimSensitivity = clamp(aim, 0.0002, 0.02);
  }

  /** Convenience wrapper over InputState.chargeFraction using this manager's
   *  own clock (the same injectable `now` the constructor takes — tests pass
   *  a fake one, production defaults to performance.now). */
  chargeFraction(kind: 'shoot' | 'pass', minS: number, maxS: number): number {
    return this.state.chargeFraction(kind, minS, maxS, this.now());
  }

  get controlMap(): ControlMap {
    return this.map;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Keep Tab/Space/Ctrl from scrolling or moving focus while playing.
    if (this.map.keys[e.code]) e.preventDefault();
    this.state.feedKeyDown(e.code, this.map, this.now());
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.state.feedKeyUp(e.code, this.map, this.now());
  };
  private onMouseDown = (e: MouseEvent): void => {
    this.state.feedMouseDown(e.button, this.map, this.now());
  };
  private onMouseUp = (e: MouseEvent): void => {
    this.state.feedMouseUp(e.button, this.map, this.now());
  };
  private onMouseMove = (e: MouseEvent): void => {
    if (document.pointerLockElement === this.target) {
      this.state.feedMouseMove(e.movementX, e.movementY);
    }
  };
  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.state.feedWheel(e.deltaY);
  };
  private onContextMenu = (e: Event): void => {
    // Right-click drives a game action (pass, by default — house controls),
    // never a browser menu. Scoped to `this.target` ONLY (see attach()): the
    // rest of the page (menus, settings, browser chrome) keeps its normal
    // context menu.
    e.preventDefault();
  };

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    this.target.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('mousemove', this.onMouseMove);
    this.target.addEventListener('wheel', this.onWheel, { passive: false });
    this.target.addEventListener('contextmenu', this.onContextMenu);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.target.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    this.target.removeEventListener('wheel', this.onWheel);
    this.target.removeEventListener('contextmenu', this.onContextMenu);
  }

  /** Request pointer lock (call from a user gesture, e.g. a click on the canvas). */
  requestPointerLock(): void {
    if (document.pointerLockElement !== this.target) {
      void this.target.requestPointerLock?.();
    }
  }

  releasePointerLock(): void {
    if (document.pointerLockElement === this.target) document.exitPointerLock?.();
  }

  get pointerLocked(): boolean {
    return document.pointerLockElement === this.target;
  }
}
